import { ChangeEvent, Dispatch, SetStateAction } from 'react'

import { Icon, IconButton, Tooltip } from '@mui/material'
import Grid from '@mui/material/Grid'

import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import FormField from 'modules/FormField'

import { PayphoneLocation } from '../types'

interface Props {
	form: PayphoneLocation
	setForm: Dispatch<SetStateAction<PayphoneLocation>>
}

function PayphoneForm({ form, setForm }: Props): JSX.Element {
	const geometry = form.location.geometry

	const handleFieldChange = (field: 'name' | 'pickup_instructions', value: string) => {
		setForm(prev => ({ ...prev, [field]: value }))
	}

	const handleLocationFieldChange = (field: 'address' | 'name', value: string) => {
		setForm(prev => ({
			...prev,
			location: {
				...prev.location,
				[field]: value,
			},
		}))
	}

	const handleGeometryChange = (value: string, key: 'lat' | 'lng') => {
		setForm(prev => ({
			...prev,
			location: {
				...prev.location,
				geometry: {
					...prev.location.geometry,
					[key]: Number(value),
				},
			},
		}))
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<FormField
					label="Name"
					placeholder="Payphone name"
					value={form.name}
					onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChange('name', e.target.value)}
				/>
			</Grid>
			<Grid item xs={12}>
				<MDBox
					display="flex"
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					mb={1}
				>
					<MDTypography variant="h6" fontWeight="medium">
						Phone Numbers
					</MDTypography>
					<IconButton
						size="small"
						onClick={() =>
							setForm(prev => ({
								...prev,
								phone_numbers: [...prev.phone_numbers, ''],
							}))
						}
					>
						<Icon fontSize="small" color="info">
							add_circle_outlined
						</Icon>
					</IconButton>
				</MDBox>
				{form.phone_numbers.map((phone, index) => (
					<MDBox
						key={index}
						display="flex"
						flexDirection="row"
						alignItems="center"
						width="100%"
						mb={1}
					>
						<MDBox flex={1}>
							<FormField
								type="tel"
								placeholder="Phone number"
								value={phone}
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									const updated = [...form.phone_numbers]
									updated[index] = e.target.value
									setForm(prev => ({ ...prev, phone_numbers: updated }))
								}}
							/>
						</MDBox>
						{index > 0 && (
							<IconButton
								size="small"
								onClick={() =>
									setForm(prev => ({
										...prev,
										phone_numbers: prev.phone_numbers.filter((_, i) => i !== index),
									}))
								}
							>
								<Icon fontSize="small" color="error">
									close
								</Icon>
							</IconButton>
						)}
					</MDBox>
				))}
			</Grid>
			<Grid item xs={12}>
				<MDBox display="flex" alignItems="center" gap={0.5} mb={0.5}>
					<MDTypography variant="caption" fontWeight="regular">
						Pickup Instructions
					</MDTypography>
					<Tooltip
						title='AI will say "Please wait for your driver by the ..."'
						placement="top"
						arrow
					>
						<Icon
							color="info"
							sx={{ fontSize: '14px !important', cursor: 'pointer', lineHeight: 1 }}
						>
							info_outline
						</Icon>
					</Tooltip>
				</MDBox>
				<FormField
					placeholder="Enter customer pickup instruction here"
					value={form.pickup_instructions}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						handleFieldChange('pickup_instructions', e.target.value)
					}
				/>
			</Grid>
			<Grid item xs={12} md={4}>
				<MDBox display="flex" alignItems="center" gap={0.5} mb={0.5}>
					<MDTypography variant="caption" fontWeight="regular">
						Location Name
					</MDTypography>
					<Tooltip title={`AI will say "I see you're calling from ..."`} placement="top" arrow>
						<Icon
							color="info"
							sx={{ fontSize: '14px !important', cursor: 'pointer', lineHeight: 1 }}
						>
							info_outline
						</Icon>
					</Tooltip>
				</MDBox>
				<FormField
					placeholder="Full address name"
					value={form.location.name}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						handleLocationFieldChange('name', e.target.value)
					}
				/>
			</Grid>
			<Grid item xs={12} md={8}>
				<FormField
					label="Address"
					placeholder="Street address"
					value={form.location.address}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						handleLocationFieldChange('address', e.target.value)
					}
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<FormField
					type="number"
					label="Latitude"
					placeholder="Latitude"
					value={geometry.lat ?? ''}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						handleGeometryChange(e.target.value, 'lat')
					}
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<FormField
					type="number"
					label="Longitude"
					placeholder="Longitude"
					value={geometry.lng ?? ''}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						handleGeometryChange(e.target.value, 'lng')
					}
				/>
			</Grid>
		</Grid>
	)
}

export default PayphoneForm
